import styled from '@emotion/styled';
import {MenuItem} from '@mui/material';
import React, {
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  ChevronSmallDown,
  ChevronSmallUp,
  LoadingIcon,
} from '@ELib/assets/images';
import {mixinCenterWrapper, mixinSVGIcon} from '@ELib/styles/mixins';

import {AnimationWrapper} from './AnimationWrapper';
import {NETransparentIconButton} from './EButtons';

const LOAD_TRIGGER_HEIGHT = '2rem';

/**
 * This file is a heavily modified version of the LoadTrigger found in the ETable component written by Silvan Troxler.
 * The component was changed and fitted to the purpose of triggering loading additional data in the filter & autocomplete components
 */
export const LoadTrigger = ({
  isTop = false,
  listRef,
  onLoad,
  onLoadFinished,
  dataSize,
  hasMore,
}: {
  /** If set to true, this component is expected to be the top (not bottom) load trigger. */
  isTop?: boolean;
  listRef: RefObject<HTMLUListElement>;
  /** Custom loading handler. *Must* only resolve when the new data has finished loading. */
  onLoad: () => Promise<void>;
  onLoadFinished?: () => void;
  /** data size is needed to detect if load of parent did finish */
  dataSize: number;
  /** if there are more elements to load, will show/hide trigger (decision has to be made here, so positioning can be
   * completed after load) */
  hasMore: boolean;
}): React.ReactElement => {
  // Set the initial scroll position so that the load trigger is not visible initially.
  const loadTriggerRef = useRef<HTMLDivElement>(null);
  const [isInitialScrollingDone, setIsInitialScrollingDone] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [scrollingInfo, setScrollingInfo] = useState<{
    id: string;
    position: number;
    dataSize: number;
  }>();

  useEffect(() => {
    if (isTop && !isInitialScrollingDone && listRef.current) {
      listRef.current.scrollTop =
        loadTriggerRef.current?.scrollHeight !== undefined
          ? loadTriggerRef.current.scrollHeight
          : 0;
      setIsInitialScrollingDone(true);
    }
  }, [isInitialScrollingDone, isTop, listRef]);

  /**
   * Since we possibly have section header elements in the list we have to try to get an valid child element to
   * track position changes. If not we just take the next sibling.
   */
  const getTopAnchor = () => {
    // section headers are noHover
    const validChildElement =
      loadTriggerRef.current!.parentElement!.querySelector(
        'li[data-id]:not(.noHover)'
      );
    const next = loadTriggerRef.current?.nextElementSibling;

    return validChildElement ?? next;
  };

  const doLoad = useCallback(async () => {
    setIsLoading(true);

    const scrollAnchorRow = isTop
      ? getTopAnchor()
      : loadTriggerRef.current!.previousElementSibling!;

    const scrollAnchorId = scrollAnchorRow?.getAttribute('data-id');
    const scrollAnchorPosition = scrollAnchorRow?.getBoundingClientRect().top;
    if (scrollAnchorId && scrollAnchorPosition) {
      setScrollingInfo({
        id: scrollAnchorId!,
        position: scrollAnchorPosition,
        dataSize,
      });
    }

    await onLoad();
  }, [dataSize, isTop, onLoad]);

  useEffect(() => {
    // dataSize is needed to make sure that parent was re-rendered with the additional list elements
    if (!scrollingInfo || scrollingInfo.dataSize === dataSize) {
      return;
    }

    // set the element again as re-rendering the row list removes the previous element from the DOM
    if (isTop) {
      const scrollAnchorRow = listRef.current!.querySelector(
        `[data-id="${scrollingInfo.id}"]`
      )!;
      const currentPosition = scrollAnchorRow.getBoundingClientRect().top;
      const scrollBy = currentPosition - scrollingInfo.position;
      listRef.current!.scrollBy({top: scrollBy + 5});
    }

    onLoadFinished?.();

    setScrollingInfo(undefined);
    setIsLoading(false);
    // only trigger when rerender of parent was done, e.g. dataSize changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSize]);

  // declare intersection callback function
  // eslint-disable-next-line @typescript-eslint/no-misused-promises
  const intersectionCallback: IntersectionObserverCallback = useCallback(
    async (entries: IntersectionObserverEntry[]) => {
      if (entries.length === 0) {
        return;
      }

      const {isIntersecting} = entries[0]!;

      if (!isIntersecting || isLoading) {
        return;
      }

      await doLoad();
    },
    [doLoad, isLoading]
  );

  // set up intersection observer to detect visible load trigger
  useEffect(() => {
    const loadTriggerEl = loadTriggerRef.current;
    if (!loadTriggerEl) {
      return;
    }
    const observer = new IntersectionObserver(intersectionCallback, {
      // Threshold set 1 meaning that the intersection observer reacts to changes when the whole element is loaded
      threshold: [1],
    });
    observer.observe(loadTriggerEl);
    return () => {
      observer.unobserve(loadTriggerEl);
    };
  }, [intersectionCallback]);
  return hasMore ? (
    <div ref={loadTriggerRef} style={{height: LOAD_TRIGGER_HEIGHT}}>
      <TriggerMenuItem className='LoadTrigger' isTop={isTop}>
        {isLoading ? (
          <AnimationWrapper>
            <LoadingIcon />
          </AnimationWrapper>
        ) : isTop ? (
          <NETransparentIconButton
            onClick={() => {
              onLoad();
            }}
          >
            <ChevronSmallUp />
          </NETransparentIconButton>
        ) : (
          <NETransparentIconButton
            onClick={() => {
              onLoad();
            }}
          >
            <ChevronSmallDown />
          </NETransparentIconButton>
        )}
      </TriggerMenuItem>
    </div>
  ) : (
    <div></div>
  );
};

export const TriggerMenuItem = styled(MenuItem, {
  shouldForwardProp: (props) => props !== 'isTop',
})<{isTop?: boolean}>`
  ${({isTop}) =>
    isTop
      ? 'border-bottom: var(--regular-border-width) solid var(--color-core-black);'
      : 'border-top: var(--regular-border-width) solid var(--color-core-black);'}
  height: var(--filter-loading-spacer);
  ${mixinCenterWrapper}
  && {
    opacity: 1;
  }

  svg {
    color: var(--core-core-grey);
    ${mixinSVGIcon}
  }
`;
